'use client';
import type { CSSProperties } from 'react';
import { twMerge } from 'tailwind-merge';
import { currentDate, formatDateOnlyTime } from '@witmetrics/utils/time';
import { useToggle } from '@/hooks/useToggle';
import { useNumber } from '@/hooks/useNumber';
import { useMountEffect } from '@/hooks/useMountEffect';
import Tooltip from '@/components/Tooltip';
import { classes, getTimeOffset, styles } from './utils';

export type CurrentTimeProps = {
  className?: string;
  hourHeight: number;
  offsetTop: string;
  showDot?: boolean;
  style?: CSSProperties;
};

export default function CurrentTime({
  className,
  hourHeight,
  offsetTop,
  style = {},
  showDot,
  ...props
}: CurrentTimeProps) {
  const [offset, setOffset] = useNumber(getTimeOffset(hourHeight));
  const [isTooltipShown, toggleTooltip] = useToggle(); // Toggling tooltip forces it to render the most current time

  useMountEffect(() => {
    const interval = setInterval(() => {
      setOffset(getTimeOffset(hourHeight));
    }, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  });

  return (
    <div
      style={{ ...styles.wrapper(offset, offsetTop), ...style }}
      className={twMerge(classes.wrapper, className)}
      {...props}>
      {showDot && (
        <Tooltip
          open={isTooltipShown}
          title={formatDateOnlyTime(currentDate())}
          onOpen={() => toggleTooltip(true)}
          onClose={() => toggleTooltip(false)}>
          <div className={classes.dot} />
        </Tooltip>
      )}
    </div>
  );
}
